import React, { useEffect, useState } from "react";
import Footer from "../components/Shared/Footer";
import Navbar from "../components/NavBar/Navbar";
import { useStripe } from "@stripe/react-stripe-js";
import { useOrderContext } from "./../hooks/useContext/useOrderContext";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import { Grid, Box, CircularProgress, Button } from "@mui/material";
import { styled } from "@mui/system";
import Divider from "@mui/material/Divider";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoDisturbAltIcon from '@mui/icons-material/DoDisturbAlt';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useEmail } from "../hooks/useEmail";


const GridContainerMain = styled("div")({
  width: "100%",
  paddingBottom: "1%",
  minHeight: "100vh",
  border: 0,
  borderRadius: "1px",
  marginTop: "20px",
  paddingLeft: "25%",
  paddingRight: "25%",
  "@media (max-width: 1600px)": {
    paddingLeft: "20%",
    paddingRight: "20%",
  },
  "@media (max-width: 960px)": {
    paddingLeft: "1%",
    paddingRight: "1%",
  },
  "@media (max-width: 600px)": {
    paddingLeft: "1%",
    paddingRight: "1%",
  },
});


function Complete() {
  const stripe = useStripe();
  const { order, name, email, paymentMethod, dispatch } = useOrderContext();
  const { sendEmail } = useEmail();
  const [status, setStatus] = useState("");
  const [placedOrder, setPlacedOrder] = useState(order);
  const [isLoading, setIsLoading] = useState(false);
  const theme = useTheme();
  const [orderEmail] = useState(email);
  const [orderName] = useState(name);

  const successIcon = <CheckCircleIcon sx={{ fontSize: 50, color: theme.palette.common.success }}/>;
  const errorIcon = <DoDisturbAltIcon sx={{ fontSize: 50, color: theme.palette.common.error }}/>;
  const processingIcon = <AutorenewIcon sx={{ fontSize: 50, color: theme.palette.common.black }}/>;

  const STATUS_CONTENT_MAP = {
    succeeded: { text: `Dziękujemy za Twoje zamówienie, ${orderName}`, icon: successIcon},
    processing: { text: "Twoja płatność jest w trakcie przetwarzania", icon: processingIcon },
    requires_payment_method: { text: "Twoja płatność nie powiodła się, proszę spróbować ponownie", icon: errorIcon},
    default: { text: "Coś poszło nie tak, proszę spróbować ponownie", icon: errorIcon},
  };

  useEffect(() => {
    if (paymentMethod === 'inPerson' && placedOrder.length === 0) {
      console.log('No order placed. Redirecting...');
      // console.log('1 window.location.href = "/order"');
      window.location.href = "/order";
      return;
    }

    if (paymentMethod === 'inPerson') {
      // console.log('inPerson paymentMethod');
      setStatus("succeeded");
      return;
    } 
    
    if (paymentMethod === 'online') {
      if (placedOrder.length === 0) {
        console.log('2 window.location.href = "/order";');
        window.location.href = "/order";
        return;
      }
      if (!stripe) {
        // console.log('!stripe');
        setIsLoading(true);
        return;
      }
      const clientSecret = new URLSearchParams(window.location.search).get("payment_intent_client_secret");
      if (!clientSecret && paymentMethod === 'online') {
        // console.log('1 window.location.href = "/checkout";');
        window.location.href = "/checkout";
        return;
      }
      stripe.retrievePaymentIntent(clientSecret).then(({paymentIntent}) => {
        if (!paymentIntent && paymentMethod === 'online') {
          console.log("No payment intent found");
          // window.location.href = "/checkout";
          console.log('2 window.location.href = "/checkout";');
          return;
        }
        setStatus(paymentIntent.status);
        setIsLoading(false);
      })
      .catch((error) => {
        console.log("Error retrieving payment intent", error);
        setStatus("default");
        setIsLoading(false);
      });
    }

  }, [stripe, paymentMethod, order]);

    useEffect(() => {
      if (order && order.length > 0) {
        if (status === "succeeded") {
          setIsLoading(false);
          setPlacedOrder(order);
          sendEmail(orderEmail, placedOrder, orderName, paymentMethod);
          dispatch({ type: 'CLEAR_ORDER', payload: paymentMethod });
        } else if (status === "processing") {
          setIsLoading(true);
          setPlacedOrder([]);
        } else if (status === "requires_payment_method" || status === "detault" ) {
          setPlacedOrder([]);
        }
      } else {
        // window.location.href = "/order";
      }
    }, [order, status, dispatch, paymentMethod]);


  return (
    <div style={{backgroundColor: theme.palette.common.background}}>
      <Navbar />
      <GridContainerMain sx={{backgroundColor: theme.palette.common.background}}>
        <Grid container spacing={2}>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexDirection: 'column', marginTop: '50px' }}>
            {status && STATUS_CONTENT_MAP[status] ? STATUS_CONTENT_MAP[status].icon : null}
            <Typography variant="h2" align="center" style={{marginTop: "20px", marginBottom: "20px"}}>
              {status ? STATUS_CONTENT_MAP[status].text : "Ładowanie..."}
            </Typography>
            {status === "succeeded" ? (
              <Typography variant="p" align="center">
                Twoje zamówienie będzie gotowe do odbioru za 15 minut
              </Typography>
            ) : null }

            {!status && isLoading && (
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
                <CircularProgress />
              </Box>
            )}
          </Grid>
        </Grid>

        {status === "succeeded" && (
        <>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: "10px 0" }}>
            <Typography variant="p" style={{fontWeight: 600}}>
              Status Płatności:
            </Typography>
            <Typography variant="body2" style={{ marginLeft: "10px" }}>
              {paymentMethod === 'online' ? "Zapłacone" : "Płatność przy odbiorze"}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h3" align="center" style={{ marginTop: "50px", marginBottom: "10px" }}>
              Podsumowanie Zamówienia
            </Typography>
          </Grid>
          <Box sx={{ border: `1px solid ${theme.palette.component.borderColor}`, borderRadius: "20px", padding: "10px", backgroundColor: theme.palette.component.componentBackground, margin: "0 10px 0 10px" }}>
            <Grid container spacing={2}>
              {placedOrder.map((cartItem, index) => (
                <Grid item key={index} xs={12}>
                  <Grid container direction="row" spacing={0} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Grid item xs={10} sm={10} md={10} lg={10} xl={10} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'left', padding: "10px 0 10px 10px", flexDirection: 'column' }}>
                      <Typography variant="p" gutterBottom sx={{ textAlign: "left"}}>
                        {cartItem.itemName}
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "left", paddingLeft: '10px' }}>
                        {`Ilość: ${cartItem.itemQuantity} - (${cartItem.itemSize})`}
                      </Typography>
                    </Grid>
                    <Grid item xs={2} sm={2} md={2} lg={2} xl={2} sx={{textAlign: 'center'}}>
                      <Typography variant="p">
                        {`${(cartItem.itemQuantity * cartItem.itemPrice)} zł`}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Divider variant="middle" />
                </Grid>
              ))}
              <Divider variant="middle"/>
              <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: "10px" }}>
                <Typography variant="h4" sx={{marginLeft: "20px"}}>
                  Razem:
                </Typography>
                <Typography variant="h4" sx={{marginRight: "10px"}}>
                  {`${placedOrder.reduce((acc, item) => acc + item.itemQuantity * item.itemPrice, 0)} zł`}
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </>
        )}

        {status === "requires_payment_method" || status === "default" ? (
          <Grid container spacing={2}>
            <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: "10px 0 10px 10px", flexDirection: 'column' }}>
              <Typography variant="h6" align="center" style={{ marginTop: "20px" }}>
                Wolisz zapłacić osobiście?
              </Typography>
              <Typography variant="body2" align="center" style={{ marginBottom: "20px"}}>
                Edytuj swoje zamówienie w 'koszyku' i wybierz opcję 'Zapłać osobiście'.
              </Typography>
              <Button variant="greyButton" href="/cart" sx={{minWidth: "200px", marginBottom: "20px", backgroundColor: theme.palette.common.offWhite, borderColor: theme.palette.component.borderColor}}>
                Wróć do Koszyka
              </Button>
              <Button variant="primaryButton" href="/checkout" sx={{minWidth: "200px"}}>
                Wróć do Kasy
              </Button>
            </Grid>
          </Grid>
        ) : null}

      </GridContainerMain>
      <Footer />
    </div>
  );
}

export default Complete;